import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { confirmSignUp, autoSignIn, resendSignUpCode } from 'aws-amplify/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useToast } from '@/hooks/use-toast';

const verificationSchema = z.object({
  code: z.string().length(6, 'Verification code must be 6 digits').regex(/^\d+$/, 'Code must contain only numbers'),
});

type VerificationFormValues = z.infer<typeof verificationSchema>;

interface EmailVerificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  onVerified?: () => void;
}

export function EmailVerificationModal({
  open,
  onOpenChange,
  email,
  onVerified,
}: EmailVerificationModalProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isVerified, setIsVerified] = useState(false);

  const form = useForm<VerificationFormValues>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      code: '',
    },
  });

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-close modal after successful verification
  useEffect(() => {
    if (isVerified) {
      const timer = setTimeout(() => {
        onOpenChange(false);
        onVerified?.();
        setIsVerified(false);
        form.reset();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isVerified, onOpenChange, onVerified, form]);

  const onSubmit = async (data: VerificationFormValues) => {
    setIsLoading(true);

    try {
      analytics.event('email_verification_attempt', {
        email,
      });

      // Verify with AWS Amplify
      await confirmSignUp({
        username: email,
        confirmationCode: data.code,
      });

      // Auto sign in after verification
      await autoSignIn();

      // On success
      analytics.conversion('email_verification_success');
      setIsVerified(true);
    } catch (error) {
      console.error('Verification error:', error);

      // Handle Cognito errors
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      let userFriendlyMessage = 'Invalid verification code. Please try again.';

      if (errorMessage.includes('CodeMismatchException')) {
        userFriendlyMessage = 'Invalid verification code';
      } else if (errorMessage.includes('ExpiredCodeException')) {
        userFriendlyMessage = 'Verification code has expired. Please request a new one.';
      } else if (errorMessage.includes('LimitExceededException')) {
        userFriendlyMessage = 'Too many attempts. Please try again later';
      }

      form.setError('code', {
        type: 'manual',
        message: userFriendlyMessage,
      });

      toast({
        title: 'Error',
        description: userFriendlyMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);

    try {
      analytics.event('resend_verification_code', {
        email,
      });

      // Resend code with AWS Amplify
      await resendSignUpCode({
        username: email,
      });

      toast({
        title: 'Success',
        description: 'Verification code sent to your email',
      });

      // Start cooldown
      setResendCooldown(60);
      form.reset();
    } catch (error) {
      console.error('Resend error:', error);

      // Handle Cognito errors
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      let userFriendlyMessage = 'Failed to resend code. Please try again.';

      if (errorMessage.includes('LimitExceededException')) {
        userFriendlyMessage = 'Too many attempts. Please try again later';
      }

      toast({
        title: 'Error',
        description: userFriendlyMessage,
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {isVerified ? 'Email verified!' : 'Verify your email'}
          </DialogTitle>
          <DialogDescription>
            {isVerified
              ? 'Your email has been successfully verified. Redirecting...'
              : `We sent a 6-digit verification code to ${email}`}
          </DialogDescription>
        </DialogHeader>

        {isVerified ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
            <p className="text-sm text-muted-foreground">Taking you to your dashboard...</p>
          </div>
        ) : (
          <>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="123456"
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          maxLength={6}
                          disabled={isLoading}
                          className="text-center text-2xl tracking-widest"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Email'
                  )}
                </Button>
              </form>
            </Form>

            <div className="text-center mt-6">
              <p className="text-sm text-muted-foreground mb-2">Didn't receive the code?</p>
              <Button
                variant="link"
                className="px-0 text-sm h-auto"
                type="button"
                onClick={handleResendCode}
                disabled={isResending || resendCooldown > 0}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend code in ${resendCooldown}s`
                ) : (
                  'Resend code'
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Check your spam folder if you don't see the email.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
